import { useEffect, useCallback, useRef, useState } from 'react';
import { Alert, Box, Fade, Portal, SxProps, Theme } from '@mui/material';
import NoConnectivityIcon from 'assets/no-connectivity.svg?react';
import { useTranslation } from 'react-i18next';
import { useDrawerHeightListener } from 'features/mapping/hooks/useDrawerHeightListener';
import { useMapEntityDrawerState } from 'features/mapping/hooks/useMapEntityDrawerState';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import { useOverlappingFeaturesState } from 'state/useOverlappingFeaturesState';
import { MAP_ENTITY_DRAWER_WIDTH } from 'features/mapping/components/MapEntityDrawer/MapEntityDrawer.constants';
import { OVERLAPPING_FEATURES_FOOTER_DESKTOP_MIN_HEIGHT_PX } from 'features/mapping/mapping.constants';
import WarningIcon from '@mui/icons-material/Warning';
import {
  MAP_DOM_ELEMENT_ID,
  SEARCH_DRAWER_WIDTH,
  STATUS_ALERT_OFFSET_CSS_VAR,
} from '../../constants';
import { useMobileDrawerElements } from './useMobileDrawerElements';
import {
  DESKTOP_FOOTER_HEIGHT_PX,
  SHEET_OVERLAP_PX,
  STATUS_ALERT_CONTROLS_OFFSET_PX,
  STATUS_ALERT_TRANSITION_DURATION_MS,
} from './PersistentStatusAlert.constants';

export type PersistentStatusAlertVariant =
  | 'network'
  | 'unstructured_evac_zones';

type PersistentStatusAlertProps = {
  /** `null` hides the alert; the last message stays through the fade-out. */
  variant: PersistentStatusAlertVariant | null;
  trackBottomSheetHeight?: boolean;
};

const getMapContainer = (): HTMLElement | null =>
  document.getElementById(MAP_DOM_ELEMENT_ID);

const getDesktopCoveredPx = (options: {
  isEntityDrawerOpen: boolean;
  isSearchDrawerOpen: boolean;
}): number => {
  const { isEntityDrawerOpen, isSearchDrawerOpen } = options;

  if (!isEntityDrawerOpen) return 0;

  return isSearchDrawerOpen
    ? MAP_ENTITY_DRAWER_WIDTH - SEARCH_DRAWER_WIDTH
    : MAP_ENTITY_DRAWER_WIDTH;
};

const getContainerSx = (options: {
  trackBottomSheetHeight: boolean;
  desktopCoveredPx: number;
  /** Lifts the alert above `OverlappingLayersNavFooter` on desktop when it's showing. */
  desktopOverlapFooterPx: number;
}): SxProps<Theme> => {
  const { trackBottomSheetHeight, desktopCoveredPx, desktopOverlapFooterPx } =
    options;

  return (theme) => ({
    // Mobile stays viewport-fixed and rides the sheet edge. Desktop is
    // absolute inside `#map-container` and clamped to the map area still
    // visible beside the drawers.
    position: trackBottomSheetHeight ? 'fixed' : 'absolute',
    bottom: trackBottomSheetHeight ? 0 : desktopOverlapFooterPx,
    left: 0,
    width: trackBottomSheetHeight
      ? '100%'
      : `calc(100% - ${desktopCoveredPx}px)`,
    padding: 0,
    pointerEvents: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    // A mobile z-index would do nothing: PageLayout's fixed stacking context
    // keeps this below the sheets, which are portaled to `<body>`. Desktop
    // stays under the MUI Drawers.
    zIndex: trackBottomSheetHeight ? undefined : theme.zIndex.drawer - 1,
    transition: theme.transitions.create(['width', 'bottom'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  });
};

const getAlertSx =
  (trackBottomSheetHeight: boolean): SxProps<Theme> =>
  (theme) => ({
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: 500,
    boxShadow: theme.shadows[3],
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    '& .MuiAlert-icon': {
      marginRight: '6px',
    },
    ...(trackBottomSheetHeight
      ? {
          paddingTop: '3px',
          paddingBottom: '12px',
        }
      : {
          // Fixed footer height. MUI pads the root, icon and message
          // separately, so all three have to be flattened to hit it.
          height: DESKTOP_FOOTER_HEIGHT_PX,
          paddingTop: 0,
          paddingBottom: 0,
          borderRadius: 0,
          '& .MuiAlert-icon, & .MuiAlert-message': {
            paddingTop: 0,
            paddingBottom: 0,
          },
        }),
  });

export const PersistentStatusAlert = (
  props: PersistentStatusAlertProps,
): JSX.Element => {
  const { trackBottomSheetHeight = false, variant } = props;
  const { open: isEntityDrawerOpen } = useMapEntityDrawerState();
  const { open: isSearchDrawerOpen } = useSearchDrawerState();
  const { hasOverlappingFeatures } = useOverlappingFeaturesState();

  const open = variant !== null;

  // Cached so the content doesn't blank mid-fade: `variant` goes `null` in the
  // same update that closes the alert.
  const [renderedVariant, setRenderedVariant] =
    useState<PersistentStatusAlertVariant>(variant ?? 'network');
  if (variant !== null && variant !== renderedVariant) {
    setRenderedVariant(variant);
  }

  // The alert is still on screen while it fades out, so the space it reserves
  // and the sheet tracking outlive `open`.
  const [isExiting, setIsExiting] = useState(false);
  const isPresent = open || isExiting;

  const handleEnter = useCallback((): void => {
    setIsExiting(false);
  }, []);

  const handleExit = useCallback((): void => {
    setIsExiting(true);
  }, []);

  const handleExited = useCallback((): void => {
    setIsExiting(false);
  }, []);

  const desktopCoveredPx = trackBottomSheetHeight
    ? 0
    : getDesktopCoveredPx({ isEntityDrawerOpen, isSearchDrawerOpen });

  // OverlappingLayersNavFooter is `position: fixed` at the true viewport bottom,
  // independent of this alert. Desktop-only: mobile's dialogs already clear it.
  // `+ 4`: visually tuned gap.
  const desktopOverlapFooterPx =
    !trackBottomSheetHeight && hasOverlappingFeatures
      ? OVERLAPPING_FEATURES_FOOTER_DESKTOP_MIN_HEIGHT_PX + 4
      : 0;

  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const visibleHeightsRef = useRef({ entity: 0, search: 0 });

  const applyOffset = useCallback((): void => {
    if (!trackBottomSheetHeight || !containerRef.current) return;
    const { entity, search } = visibleHeightsRef.current;
    const offsetPx = SHEET_OVERLAP_PX - Math.max(entity, search);
    containerRef.current.style.transform = `translate3d(0, ${offsetPx}px, 0)`;
  }, [trackBottomSheetHeight]);

  const setContainerRef = useCallback(
    (node: HTMLDivElement | null): void => {
      containerRef.current = node;
      applyOffset();
    },
    [applyOffset],
  );

  // Not worth running while the alert is hidden.
  const { entityDrawerElement, searchDrawerElement } = useMobileDrawerElements(
    trackBottomSheetHeight && isPresent,
  );

  // Visible height, not the raw height: a sheet pins its height to the target
  // snap point while opening, so tracking that parks the alert at the final
  // position and waits for the sheet to catch up.
  useDrawerHeightListener(entityDrawerElement, null, (_heightPx, visiblePx) => {
    visibleHeightsRef.current.entity = visiblePx;
    applyOffset();
  });

  useDrawerHeightListener(searchDrawerElement, null, (_heightPx, visiblePx) => {
    visibleHeightsRef.current.search = visiblePx;
    applyOffset();
  });

  // A sheet that left the DOM contributes nothing. The entity sheet unmounts a
  // frame after it closes, and is swapped for a fresh node when navigating
  // between entity routes, so without this its last height could linger.
  useEffect(() => {
    if (!entityDrawerElement) visibleHeightsRef.current.entity = 0;
    if (!searchDrawerElement) visibleHeightsRef.current.search = 0;
    applyOffset();
  }, [entityDrawerElement, searchDrawerElement, applyOffset]);

  // Publish the space this alert takes at the bottom of the map, like
  // `DownloadAppBanner` does, so the bottom map controls can stay visible.
  // `isPresent` rather than `open`, so they don't drop back through it mid-fade.
  useEffect(() => {
    if (!isPresent) return undefined;

    const { style } = document.documentElement;
    const baseOffsetPx = trackBottomSheetHeight
      ? STATUS_ALERT_CONTROLS_OFFSET_PX.mobile
      : STATUS_ALERT_CONTROLS_OFFSET_PX.desktop;
    const offsetPx = baseOffsetPx + desktopOverlapFooterPx;

    style.setProperty(STATUS_ALERT_OFFSET_CSS_VAR, `${offsetPx}px`);

    // Reset rather than remove, so another map's controls aren't left pushed up.
    return () => {
      style.setProperty(STATUS_ALERT_OFFSET_CSS_VAR, '0px');
    };
  }, [isPresent, trackBottomSheetHeight, desktopOverlapFooterPx]);

  const alert = (
    <Box
      ref={setContainerRef}
      sx={getContainerSx({
        trackBottomSheetHeight,
        desktopCoveredPx,
        desktopOverlapFooterPx,
      })}
    >
      <Fade
        in={open}
        unmountOnExit
        timeout={STATUS_ALERT_TRANSITION_DURATION_MS}
        easing={{ enter: 'ease-in', exit: 'ease-in' }}
        onEnter={handleEnter}
        onExit={handleExit}
        onExited={handleExited}
      >
        <Alert
          severity="watchduty-minor"
          icon={
            renderedVariant === 'network' ? (
              <NoConnectivityIcon width={16} height={16} />
            ) : (
              <WarningIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={getAlertSx(trackBottomSheetHeight)}
          aria-labelledby="persistent-status-message"
        >
          <span id="persistent-status-message">
            {renderedVariant === 'network'
              ? t('persistentStatus.disconnected')
              : t('persistentStatus.unstructuredEvacZones')}
          </span>
        </Alert>
      </Fade>
    </Box>
  );

  if (trackBottomSheetHeight) return alert;

  // Desktop is portaled into the map, matching `PoiDialog` and the
  // desktop `MapEntityDrawer`.
  return <Portal container={getMapContainer}>{alert}</Portal>;
};
